import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Upload, Trash2, Star } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminTestimonials = () => {
  const navigate = useNavigate();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    }
  };

  const handleImageUpload = async (e, testimonialId) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const imageUrl = `${BACKEND_URL}${response.data.url}`;
      
      const testimonial = testimonials.find(t => t.id === testimonialId);
      if (testimonial) {
        testimonial.image = imageUrl;
        await handleUpdate(testimonialId, { image: imageUrl });
      }
      
      toast.success('Image uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload image');
      console.error(error);
    }
  };

  const handleUpdate = async (id, updates) => {
    try {
      await axios.put(`${API}/testimonials/${id}`, updates);
      fetchTestimonials();
      toast.success('Testimonial updated!');
    } catch (error) {
      toast.error('Failed to update testimonial');
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;
    
    try {
      await axios.delete(`${API}/testimonials/${id}`);
      fetchTestimonials();
      toast.success('Testimonial deleted!');
    } catch (error) {
      toast.error('Failed to delete testimonial');
      console.error(error);
    }
  };

  const handleFieldChange = (id, field, value) => {
    setTestimonials(testimonials.map(t => 
      t.id === id ? { ...t, [field]: value } : t
    ));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" onClick={() => navigate('/admin/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">Manage Testimonials</h1>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((testimonial) => (
            <Card key={testimonial.id}>
              <CardContent className="p-6">
                {/* Image */}
                <div className="relative mb-4">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-20 h-20 rounded-full object-cover mx-auto"
                  />
                  <label className="absolute bottom-0 right-1/2 translate-x-1/2 translate-y-2 cursor-pointer">
                    <div className="bg-amber-800 hover:bg-amber-900 text-white p-2 rounded-full">
                      <Upload className="w-4 h-4" />
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleImageUpload(e, testimonial.id)}
                    />
                  </label>
                </div>

                {/* Rating */}
                <div className="flex justify-center mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`w-5 h-5 cursor-pointer ${
                        star <= testimonial.rating
                          ? 'text-amber-800 fill-current'
                          : 'text-gray-300'
                      }`}
                      onClick={() => {
                        handleFieldChange(testimonial.id, 'rating', star);
                        handleUpdate(testimonial.id, { rating: star });
                      }}
                    />
                  ))}
                </div>

                {/* Name */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Client Name
                  </label>
                  <Input
                    value={testimonial.name}
                    onChange={(e) => handleFieldChange(testimonial.id, 'name', e.target.value)}
                    onBlur={() => handleUpdate(testimonial.id, { name: testimonial.name })}
                  />
                </div>

                {/* Style */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Style
                  </label>
                  <Input
                    value={testimonial.style}
                    onChange={(e) => handleFieldChange(testimonial.id, 'style', e.target.value)}
                    onBlur={() => handleUpdate(testimonial.id, { style: testimonial.style })}
                    placeholder="e.g., Box Braids"
                  />
                </div>

                {/* Review Text */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Review
                  </label>
                  <Textarea
                    value={testimonial.text}
                    onChange={(e) => handleFieldChange(testimonial.id, 'text', e.target.value)}
                    onBlur={() => handleUpdate(testimonial.id, { text: testimonial.text })}
                    rows={4}
                  />
                </div>

                {/* Delete Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDelete(testimonial.id)}
                  className="w-full border-red-500 text-red-500 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete Testimonial
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="mt-8 bg-amber-50 border-amber-200">
          <CardContent className="p-6">
            <p className="text-gray-700">
              💡 <strong>Tip:</strong> Click on any field to edit testimonials. Upload client photos, adjust ratings by clicking stars, 
              and edit review text. Changes save automatically.
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminTestimonials;
